import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
//pagination
import { useTrackVisibility } from 'react-intersection-observer-hook';
//helpers
import { capitalize } from 'helpers';
// api
import { getProducts, getProductsFast } from 'api/products';
import { getBooksByLanguages } from 'api/libros';
import { getElementsList } from 'api/lists';
//constants
import { othersFilters } from 'constants';
//components
import Head from 'components/metatags_helmet/metatags';
import NoResults from 'components/userzone/NoResults';
import ItemList from 'components/itemList/item';
import ListItemsFilter from './listItemsFilter';
import PaginationLoader from 'components/loader/PaginationLoader';
import BreadCrumb from 'components/breadcrumb/BreadCrumb.js';
//styles
import './list.scss';

const List = () => {
  const params = useParams();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [maxPage, setMaxPage] = useState(null);
  const { t } = useTranslation(['backoffice', 'list', 'header']);
  const title = t('busqueda');
  const [filters, setFilters] = useState({
    selectFilter: null,
    typebook: '',
    languageFilter: '',
    typeIbic: '',
    products: '',
    titulo: '',
    ean: '',
    autor: '',
    editorial: '',
    books: false,
    is_ebook: null,
    products_by_list: null,
  });
  const [breadcrumbTitle, setBreadcrumbTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [ref, { isVisible }] = useTrackVisibility();
  const location = useLocation();
  const { ibics, list } = location.state || {};
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  let nameList = queryParams.get('title') || '';
  nameList = nameList?.charAt(0).toUpperCase() + nameList?.slice(1);

  const getLoadData = async (page = 1, fieldFilter) => {
    setIsLoading(true);
    const payload = {
      page: page,
      page_size: 18,
      book_ibic: fieldFilter.typeIbic || '',
      book_type: fieldFilter.typebook || '',
      order_by: fieldFilter.selectFilter || null,
      idioma: fieldFilter.languageFilter || '',
      search_all_fields: fieldFilter.products || '',
      titulo: fieldFilter.titulo || '',
      ean: fieldFilter.ean || '',
      autor: fieldFilter.autor || '',
      editorial: fieldFilter.editorial || '',
      is_ebook: fieldFilter.is_ebook,
      fields: 'id,image,titulo,price,fecha_public,type,languages,authors,has_stock',
    };
    try {
      let response;
      const onlySearchAllFields =
        fieldFilter.products &&
        !fieldFilter.books &&
        !fieldFilter.products_by_list &&
        !fieldFilter.typeIbic &&
        !fieldFilter.selectFilter &&
        !fieldFilter.titulo &&
        !fieldFilter.ean &&
        !fieldFilter.autor &&
        !fieldFilter.editorial &&
        fieldFilter.is_ebook == null;
      if (onlySearchAllFields) {
        const value = fieldFilter.products.trim();
        const isEan = /^\d{13}$/.test(value);
        const isAuthor = /[a-zA-Z]+\s*,\s*[a-zA-Z]+/.test(value);
        const isEditorial = /^[A-Z][A-Z\s\.,\-áéíóúÁÉÍÓÚÑñ]+$/.test(value) && value.length > 10;
        const isTitulo = !isEan && !isAuthor && !isEditorial;
        const fastPayload = {
          page: page,
          page_size: 18,
          fields: 'id,ean,titulo,editorial,authors',
        };
        if (isEan) {
          fastPayload.ean = value;
        } else if (isAuthor) {
          fastPayload.autor = value;
        } else if (isEditorial) {
          fastPayload.editorial = value;
        } else if (isTitulo) {
          fastPayload.titulo = value;
        }
        if (fieldFilter.languageFilter) {
          fastPayload.idioma = fieldFilter.languageFilter;
        }
        if (fieldFilter.typebook) {
          fastPayload.book_type = fieldFilter.typebook;
        }
        if (fieldFilter.typeIbic) {
          fastPayload.book_ibic = fieldFilter.typeIbic;
        }
        response = await getProductsFast(fastPayload);
      } else if (fieldFilter.books) {
        response = await getBooksByLanguages(payload);
      } else if (fieldFilter.products_by_list) {
        response = await getElementsList(list, payload);
      } else {
        response = await getProducts(payload);
      }
      const { status, data } = response;
      if (status === 200) {
        if (data?.results.length > 0) {
          setItems((prev) => (page === 1 ? data.results : [...(prev || []), ...data.results]));
          setMaxPage(data.total_pages);
          setNextPage(data.next);
        } else {
          setItems([]);
        }
      }
    } catch (error) {
      console.log('Error en getLoadData:', error);
    }
    setIsLoading(false);
  };

  const goUp = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const searchParams = {
      selectFilter: null,
      typeIbic: params.type === 'materias' ? ibics : '',
      typebook: params.type === 'ebooks' ? 'ebook' : '',
      products_by_list: params.type === 'products_by_list' ? 'products_by_list' : null,
      languageFilter: '',
      is_ebook: null,
    };
    if (params.type === 'search') {
      const fields = new URLSearchParams(location.search);
      fields.forEach((value, key) => {
        if (!othersFilters.includes(key)) {
          searchParams[key] = value;
        } else {
          switch (key) {
            case othersFilters[0]:
              searchParams[othersFilters[0]] = true;
              searchParams[othersFilters[1]] = value;
              break;
            case othersFilters[2]:
              if (searchParams.books) {
                searchParams[othersFilters[2]] = value === 'ebook';
              } else {
                searchParams['typebook'] = value;
              }
              break;
            case othersFilters[3]:
              searchParams['languageFilter'] = value;
              break;

            default:
              searchParams[othersFilters[1]] = value;
              break;
          }
        }
      });
    }

    setFilters(searchParams);
    setPage(1);
    setFiltersInitialized(true);
    switch (params.type) {
      case 'search':
        setBreadcrumbTitle(t('list:seeker'));
        break;
      case 'ebooks':
        setBreadcrumbTitle('Ebooks');
        break;
      case 'materias':
        setBreadcrumbTitle(t('matters', { ns: 'header' }));
        break;
      case 'products_by_list':
        setBreadcrumbTitle(nameList);
        break;
      case 'recomiendan':
        setBreadcrumbTitle(t('list:recommend'));
        break;
      default:
        setBreadcrumbTitle(t('news', { ns: 'list' }));
        break;
    }
  }, [params.type, ibics, location.search]);

  useEffect(() => {
    if (filtersInitialized) {
      setItems([]);
      getLoadData(1, filters);
      goUp();
    }
  }, [filters, filtersInitialized]);

  useEffect(() => {
    if (isVisible && page < maxPage && nextPage && items.length > 0) {
      let newValuePage = page + 1;
      getLoadData(newValuePage, filters);
      setPage(newValuePage);
    }
  }, [isVisible]);

  const handleFilterChange = (type, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [type]: value,
    }));
  };

  const cleanSearch = useCallback(
    (filter = '') => {
      let cleanSearchValue = {
        selectFilter: null,
        typebook: params.type === 'ebooks' ? filter : '',
        typeIbic: params.type === 'materias' ? ibics : '',
        products_by_list: params.type === 'products_by_list' ? 'products_by_list' : null,
        languageFilter: '',
        is_ebook: null,
      };
      if (params.type === 'search') {
        cleanSearchValue = { ...filters };
        cleanSearchValue['selectFilter'] = null;
      }
      setFilters(cleanSearchValue);
    },
    [params.type, ibics, location.search, filters],
  );

  return (
    <div className="container-fluid px-0 mainDiv">
      <Head title={nameList || title} />
      <Container fluid className="px-3 mx-0">
        <Row className="justify-content-start pl-3 mx-0">
          <Col lg={12} className="mx-0 breadcrumb-container">
            <BreadCrumb breadcrumbTitle={capitalize(breadcrumbTitle)} />
          </Col>
          <Col lg={2} className="px-0 mx-0">
            <ListItemsFilter
              handleFilter={handleFilterChange}
              cleanSearch={() => cleanSearch('ebook')}
              filters={filters}
              nameList={nameList}
              id={params.id}
            />
          </Col>
          <Col lg={10} className={isLoading ? 'd-flex flex-wrap' : 'd-flex flex-wrap fit-height'}>
            {items?.length > 0 ? (
              <Row className={maxPage === 1 ? 'item-container w-100' : 'item-container overflow-hidden'}>
                {items.map((item, index) => (
                  <Col md={4} lg={2} sm={6} key={`itmes#${index}`} className="margin-list-responsive">
                    <ItemList key={index} data={item} />
                  </Col>
                ))}
              </Row>
            ) : isLoading ? (
              <PaginationLoader visibility={isLoading} />
            ) : (
              <NoResults className="noFavorites" />
            )}

            {!isLoading && nextPage && <div aria-hidden="true" ref={ref}></div>}
            {items.length > 0 && (
              <div className="container">
                <PaginationLoader visibility={isLoading} />
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default List;